import * as React from "react";
import { memo, useCallback, useMemo } from "react";
import { Paper, styled } from "@mui/material";
import { MobileView } from "../../../util/aKaunGeneralUtil";
import { PatientChartSectionTitle } from "./PatientChartSectionTitle";
import { PatientCaseGrouping } from "../../../context/diagnosticAgent";
import {
  RosebudRouteSetting,
  RosebudRoutesSettings,
} from "../util/rosebudRoutesSettings";
import { RosebudColorMap } from "../rosebud/rosebudColorMap";
import { useRouteHelper } from "../useRouteHelper";
import { PatientChartGroupedFeatures } from "./patientChartGroupedFeatures";
import { PatientChartAgeGender } from "./patientChartAgeGender";
import { PatientChartVitalSignsDesktop } from "./patientChartVitalSignsDesktop";
import { DataInputMobileSidePadding } from "../common/styles/consts";
import { useGetPanelAbnormalQf } from "../hooks/useGetPanelAbnormalQf";
import { KahunPanels } from "shared";
import { AgentRoute } from "../util/agentRoutes";

//region [[ Styles ]]
const PatientCaseContainerView = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  marginTop: "10px",
  [theme.breakpoints.up(MobileView)]: {
    height: `100%`,
    overFlowY: "hidden",
    borderRadius: "0px",
    boxShadow: "none",
    border: "none",
  },
  "& .MuiListItem-root.Mui-selected": {
    backgroundColor: "white",
  },
}));

const PatientSectionContainer = styled(Paper)(({ theme }) => ({
  marginBottom: "10px",
  backgroundColor: "white",
  borderRadius: "2px",
  marginRight: "10px",
  marginLeft: "10px",
  boxShadow: "0px 3px 9px  rgba(0, 0, 0, 0.12)",
  transition: "border-width 0.3s ease-in",
  borderLeft: "0px solid",
  "&.selected": {
    borderLeft: `5px solid ${RosebudColorMap.green2}`,
  },
  [theme.breakpoints.down(MobileView)]: {
    marginRight: `${DataInputMobileSidePadding}px`,
    marginLeft: `${DataInputMobileSidePadding}px`,
    border: "1px solid rgb(221, 221, 221)",
    boxShadow: "rgba(0, 0, 0, 0.12) 0px 1px 4px",
    borderRadius: "8px",
  },
}));

export const MaleFemaleIcon = styled("img")(
  ({ theme, height }: { theme?: any; height?: string }) => ({
    height: height ? height + "px" : "60",
    marginTop: "10px",
  }),
);

export const PatientChartSection = styled("div")(({ theme }) => ({
  marginBottom: "15px",
  borderBottom: "1px solid #e3ebec",
  "&:last-of-type": {
    border: "none",
  },

  [theme.breakpoints.down(MobileView)]: {
    marginBottom: "0px",
    border: "none",
    borderBottom: "none",
  },
}));

export const MaleFemaleAvatar = styled("div")(({ theme }) => ({
  width: "60px",
  height: "60px",
}));

//endregion [[ Styles ]]

//region [[ Props ]]
export interface PatientCaseProps {
  className?: string;
  gotoDataInput?: (
    panel: PatientCaseGrouping,
    categoryid?: string,
    toMyPatient?: boolean,
  ) => void;
  gotoDemographic?: () => void;
  loading?: boolean;
  onClick?: (PatientCaseStep: RosebudRouteSetting) => void;
}

//endregion [[ Props ]]

//region [[ Functions ]]

//endregion [[Functions]]

// Filter steps that have groups

export const PatientCaseContainer = memo(
  ({ gotoDataInput, loading, onClick, ...props }: PatientCaseProps) => {
    const { isAllNormal: isAllVitalSignsNormal } = useGetPanelAbnormalQf(
      PatientCaseGrouping.CLINICAL_FEATURES,
      KahunPanels.VitalSigns,
    );
    const { navigateTo, getCurrentRouteSettings } = useRouteHelper();
    const currentStep = getCurrentRouteSettings();

    const sections = useMemo(
      () =>
        RosebudRoutesSettings.filter(
          (s, index) => s.grouping && s.isWizardStep,
        ),
      [],
    );

    const ageAndGender = useMemo(
      () =>
        RosebudRoutesSettings.find(
          (setting) => setting.route === AgentRoute.AgeAndGender,
        ),
      [],
    );

    const createChartSectionTitleClickHandler = useCallback(
      (step: RosebudRouteSetting, subGroupId?: string) => {
        return () => {
          if (onClick) {
            onClick(step);
          }
          navigateTo({ step, subGroupId });
        };
      },
      [onClick, navigateTo],
    );

    const markAsSelected = useCallback(
      (step) => (step.route === currentStep?.route ? " selected" : ""),
      [currentStep],
    );

    const stepGroupingToSection = useCallback(
      (step) => (
        <PatientSectionContainer
          elevation={0}
          key={`patient-section-${step.route}`}
          className={`patient-section${markAsSelected(step)}`}
        >
          <PatientChartSectionTitle
            titleText={step.displayText}
            onClick={createChartSectionTitleClickHandler(step)}
            showCheck={
              step.grouping === PatientCaseGrouping.VITAL_SIGNS &&
              isAllVitalSignsNormal
            }
          />
          {step.grouping !== PatientCaseGrouping.VITAL_SIGNS ? (
            <PatientChartGroupedFeatures
              section={step.grouping}
              step={step}
              onClick={onClick}
              showAllSubGroups={false}
            />
          ) : (
            <PatientChartVitalSignsDesktop gotoDataInput={gotoDataInput} />
          )}
        </PatientSectionContainer>
      ),
      [
        gotoDataInput,
        isAllVitalSignsNormal,
        markAsSelected,
        onClick,
        createChartSectionTitleClickHandler,
      ],
    );

    return (
      <PatientCaseContainerView className={props.className}>
        <PatientSectionContainer
          className={`patient-section${markAsSelected(ageAndGender)}`}
        >
          <PatientChartAgeGender />
        </PatientSectionContainer>
        {sections.map(stepGroupingToSection)}
      </PatientCaseContainerView>
    );
  },
);
